import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, GripVertical, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
function SortableDisciplineRow({ id, discipline, canEdit, canReorder, onView, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: !canReorder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-stone-100 hover:bg-stone-50">
      <td className="w-8 py-3 px-2">
        {canReorder ? (
          <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
            <GripVertical className="h-4 w-4 text-stone-400" />
          </button>
        ) : (
          <span className="text-stone-200">
            <GripVertical className="h-4 w-4" />
          </span>
        )}
      </td>
      <td className="py-3 px-2 font-medium">{discipline.discipline}</td>
      <td className="py-3 px-2 text-sm">{discipline.type}</td>
      <td className="py-3 px-2 text-sm">{discipline.competitor_type}</td>
      <td className="py-3 px-2 text-sm text-center">{discipline.own_music ? 'Yes' : 'No'}</td>
      <td className="py-3 px-2">
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => onView(discipline)} className="action-icon" title="View" data-testid={`view-discipline-${discipline.discipline_id}`}>
            <Eye className="h-4 w-4" />
          </button>
          {canEdit && (
            <>
              <button onClick={() => onEdit(discipline)} className="action-icon" title="Edit" data-testid={`edit-discipline-${discipline.discipline_id}`}>
                <Edit2 className="h-4 w-4" />
              </button>
              <button onClick={() => onDelete(discipline.discipline_id)} className="action-icon" title="Delete" data-testid={`delete-discipline-${discipline.discipline_id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export function DisciplinesTab({ disciplines, setDisciplines, fetchDisciplines, handleDragEnd, sensors, currentUser }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [formData, setFormData] = useState({ 
    discipline: '', 
    type: '', 
    competitor_type: '',
    own_music: false
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  
  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;
  
  // Check if user can edit a specific item
  const canEditItem = (item) => {
    return isSuperUser || item.created_by === currentUserId;
  };
  
  // Find editable items for "Save, Edit Next"
  const editableItems = disciplines.filter(d => canEditItem(d));
  
  const resetForm = () => {
    setFormData({ discipline: '', type: '', competitor_type: '', own_music: false });
    setEditingItem(null);
    setEditingIndex(-1);
    setViewOnly(false);
  };
  
  const handleEdit = (discipline) => {
    const idx = editableItems.findIndex(d => d.discipline_id === discipline.discipline_id);
    setFormData({
      discipline: discipline.discipline,
      type: discipline.type,
      competitor_type: discipline.competitor_type,
      own_music: discipline.own_music || false
    });
    setEditingItem(discipline);
    setEditingIndex(idx);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (discipline) => {
    handleEdit(discipline);
    setViewOnly(true);
  };
  
  const handleSubmit = async (e, editNext = false) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.patch(`${API}/content/disciplines/${editingItem.discipline_id}`, formData, { withCredentials: true });
        toast.success('Discipline updated!');
        
        if (editNext && editingIndex < editableItems.length - 1) {
          // Move to next editable item
          await fetchDisciplines();
          const nextItem = editableItems[editingIndex + 1];
          if (nextItem) {
            setFormData({
              discipline: nextItem.discipline,
              type: nextItem.type,
              competitor_type: nextItem.competitor_type,
              own_music: nextItem.own_music || false
            });
            setEditingItem(nextItem);
            setEditingIndex(editingIndex + 1);
            return;
          }
        }
      } else {
        await axios.post(`${API}/content/disciplines`, formData, { withCredentials: true });
        toast.success('Discipline created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchDisciplines();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Permission denied');
      } else {
        toast.error('Failed to save discipline');
      }
    }
  };
  
  const handleDeleteClick = (id) => {
    const discipline = disciplines.find(d => d.discipline_id === id);
    setItemToDelete(discipline);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/content/disciplines/${itemToDelete.discipline_id}`, { withCredentials: true });
      toast.success('Discipline deleted');
      fetchDisciplines();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Permission denied');
      } else {
        toast.error('Failed to delete discipline');
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Disciplines</h2>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-discipline-button">
          <Plus className="mr-2 h-4 w-4" />
          Add Discipline
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create new disciplines and edit/delete your own items. Items created by others are view-only.
        </div>
      )}
      
      <Card className="p-6">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'disciplines')}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="w-8"></th>
                  <th className="text-left py-3 px-2 font-semibold">Discipline</th>
                  <th className="text-left py-3 px-2 font-semibold">Type</th>
                  <th className="text-left py-3 px-2 font-semibold">Competitor Type</th>
                  <th className="text-center py-3 px-2 font-semibold">Own Music?</th>
                  <th className="text-center py-3 px-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                <SortableContext items={disciplines.map(d => d.discipline_id)} strategy={verticalListSortingStrategy}>
                  {disciplines.map(discipline => (
                    <SortableDisciplineRow
                      key={discipline.discipline_id}
                      id={discipline.discipline_id}
                      discipline={discipline}
                      canEdit={canEditItem(discipline)}
                      canReorder={isSuperUser}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
            {disciplines.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No disciplines yet. Create your first one!
              </div>
            )}
          </div>
        </DndContext>
      </Card>
      
      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{viewOnly ? 'View Discipline' : editingItem ? 'Edit Discipline' : 'Create Discipline'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
            <div>
              <Label>Discipline Name *</Label>
              <Input value={formData.discipline} onChange={(e) => setFormData({...formData, discipline: e.target.value})} required disabled={viewOnly} data-testid="discipline-name-input" />
            </div>
            <div>
              <Label>Type *</Label>
              <select 
                className="w-full border rounded-lg px-3 py-2"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                required
                disabled={viewOnly}
                data-testid="discipline-type-select"
              >
                <option value="">Select Type</option>
                <option value="Artistic">Artistic</option>
                <option value="Figures">Figures</option>
              </select>
            </div>
            <div>
              <Label>Competitor Type *</Label>
              <select 
                className="w-full border rounded-lg px-3 py-2"
                value={formData.competitor_type}
                onChange={(e) => setFormData({...formData, competitor_type: e.target.value})}
                required
                disabled={viewOnly}
                data-testid="discipline-competitor-type-select"
              >
                <option value="">Select Competitor Type</option>
                <option value="Individual">Individual</option>
                <option value="Pairs/Couple">Pairs/Couple</option>
                <option value="Teams">Teams</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={formData.own_music} 
                onCheckedChange={(checked) => setFormData({...formData, own_music: checked})}
                disabled={viewOnly}
                data-testid="discipline-own-music-checkbox"
              />
              <Label className="cursor-pointer">Own Music?</Label>
            </div>
            {!viewOnly && (
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 btn-primary" data-testid="discipline-save-button">
                  {editingItem ? 'Save' : 'Create Discipline'}
                </Button>
                {editingItem && editingIndex < editableItems.length - 1 && (
                  <Button 
                    type="button" 
                    onClick={(e) => handleSubmit(e, true)} 
                    className="flex-1"
                    variant="outline"
                    data-testid="discipline-save-next-button"
                  >
                    Save, Edit Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.discipline ? `"${itemToDelete.discipline}"` : 'this discipline'}
        loading={deleting}
      />
    </>
  );
}
